/**
 * AdminOnboardingFunnel — surfaces the tour funnel captured by
 * /api/analytics/event: started → step 1..4 completed → finished vs skipped.
 * Small, read-only, refreshable. Complements AdminJobRunsPanel.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Compass, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_LABELS = {
  started:     "Tour started",
  step_1_done: "Step 1 · Quick actions",
  step_2_done: "Step 2 · PreachKit",
  step_3_done: "Step 3 · Community",
  step_4_done: "Step 4 · Notifications",
  finished:    "Tour finished (Done)",
  skipped:     "Tour skipped",
};

export default function AdminOnboardingFunnel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchFunnel = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/analytics/onboarding-funnel?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => { fetchFunnel(); }, [fetchFunnel]);

  const startedUsers = data?.steps?.started?.unique_users || 0;
  const completionPct = Math.round((data?.completion_rate ?? 0) * 100);

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="admin-onboarding-funnel">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Compass className="w-5 h-5 text-amber-500" />
              Onboarding funnel
            </CardTitle>
            <CardDescription>How many new users complete the guided tour.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <Button
                key={d}
                size="sm"
                variant={days === d ? "default" : "outline"}
                onClick={() => setDays(d)}
                className="h-7 text-xs"
                data-testid={`funnel-range-${d}`}
              >{d}d</Button>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={fetchFunnel}
              disabled={loading}
              className="gap-1.5 h-7 text-xs"
              data-testid="refresh-onboarding-funnel"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-500" /></div>
        ) : !data ? (
          <p className="text-sm text-slate-500 py-6 text-center">Couldn't load funnel data.</p>
        ) : (
          <>
            <div className="flex items-center gap-4 p-3 rounded-lg bg-slate-950 border border-slate-800">
              <div className="flex-1">
                <p className="text-[11px] uppercase tracking-wider text-slate-500">Completion rate ({days}d)</p>
                <p className="text-3xl font-bold text-white mt-0.5" data-testid="funnel-completion-rate">{completionPct}%</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{data?.steps?.finished?.unique_users || 0} finished · {startedUsers} started</p>
              </div>
              <div className="shrink-0 w-11 h-11 rounded-full bg-amber-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-500" />
              </div>
            </div>

            <div className="rounded-lg border border-slate-800 overflow-hidden divide-y divide-slate-800" data-testid="funnel-steps-list">
              {Object.entries(data.steps).map(([key, val]) => {
                const uu = val.unique_users || 0;
                const rate = startedUsers ? Math.round((uu / startedUsers) * 100) : 0;
                return (
                  <div key={key} className="flex items-center gap-3 p-3 bg-slate-950 text-sm" data-testid={`funnel-step-${key}`}>
                    <div className="w-8 text-right text-[11px] font-mono text-slate-500 shrink-0">{rate}%</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-200 truncate">{STEP_LABELS[key] || key}</p>
                      <div className="mt-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-500" style={{ width: `${Math.min(rate, 100)}%` }} />
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-xs font-semibold text-slate-100">{uu}</div>
                      <div className="text-[10px] text-slate-500">{val.count} events</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
